import type {
  NotificationInterface,
  NotificationTemplates,
} from '@autobid/nuxt-pinia-store/types/Notification'
import { getError } from '@autobid/strapi-integration/utils/getError'
import { removeReference } from '@autobid/strapi-integration/utils/helpers'
import { defineStore } from 'pinia'
import { ref } from 'vue'

interface GetNotificationsParams {
  limit?: number
  offset?: number
}

type LoadingTypes = 'notifications' | 'readAll'

export const useNotificationStore = defineStore('useNotification', () => {
  const loading = ref<Record<LoadingTypes, boolean>>({
    notifications: false,
    readAll: false,
  })
  const fetchingFailed = ref(false)
  const hasMoreNotifications = ref(false)
  const amountOfNotificationsToSync = ref(0)
  const notifications = ref<NotificationInterface[]>([])
  const templates = ref<NotificationTemplates>({})
  const { locale } = useI18n()
  const { $trpc } = useNuxtApp()
  const push = usePush()

  const getTemplates = async () => {
    if (Object.keys(templates.value).length) {
      return templates.value
    }

    try {
      const data = await $trpc.webapi.getNotificationTemplates.query({ locale: locale.value })

      if (data) {
        templates.value = data
      }

      return templates.value
    }
    catch (error) {
      const { message, handled } = getError(error)

      if (!handled) {
        push.error(message)
      }
    }
  }

  const getNotifications = async (
    params?: GetNotificationsParams,
    refreshCurrentNotifies = false,
  ) => {
    if (
      loading.value.notifications
      || (params?.offset
        && notifications.value.length > Number(params.offset)
        && !refreshCurrentNotifies)
    ) {
      return
    }

    loading.value.notifications = true

    try {
      const data = await $trpc.webapi.getNotifications.query({
        limit: params?.limit,
        offset: params?.offset,
      })

      if (data.length) {
        await getTemplates()

        notifications.value = refreshCurrentNotifies
          ? data
          : [...notifications.value, ...data]

        hasMoreNotifications.value = data?.length >= Number(params?.limit ?? 1)
      }
      else {
        hasMoreNotifications.value = false
      }

      fetchingFailed.value = false
    }
    catch (error) {
      const { handled, message } = getError(error)

      if (!handled) {
        push.error(message)
      }

      fetchingFailed.value = true
    }
    finally {
      loading.value.notifications = false
    }

    return notifications.value
  }

  const optimisticMarkNotifiesAsRead = (ids: number[]) => {
    const stateBeforeChange = removeReference(notifications.value)

    notifications.value = notifications.value.map((el) => {
      return {
        ...el,
        status: ids.includes(Number(el.id)) ? '1' : el.status,
      }
    })

    return {
      undoOptimisticChange: () => {
        notifications.value = stateBeforeChange
      },
    }
  }

  const readNotification = async (id: number) => {
    const { undoOptimisticChange } = optimisticMarkNotifiesAsRead([id])

    try {
      const data = await $trpc.webapi.readNotification.mutate({ id })

      if (data !== 'ok') {
        undoOptimisticChange()
      }
    }
    catch (error) {
      const { handled, message } = getError(error)

      if (!handled) {
        push.error(message)
      }

      undoOptimisticChange()
    }
  }

  const readAll = async () => {
    if (loading.value.readAll)
      return

    loading.value.readAll = true

    const ids = notifications.value.map(el => Number(el.id))
    const { undoOptimisticChange } = optimisticMarkNotifiesAsRead(ids)

    try {
      const data = await $trpc.webapi.readAllNotification.mutate()

      if (data !== 'ok') {
        undoOptimisticChange()
      }
    }
    catch (error) {
      const { handled, message } = getError(error)

      if (!handled) {
        push.error(message)
      }

      undoOptimisticChange()
    }

    loading.value.readAll = false
  }

  return {
    loading,
    fetchingFailed,
    hasMoreNotifications,
    amountOfNotificationsToSync,
    notifications,
    templates,
    getTemplates,
    getNotifications,
    readNotification,
    readAll,
  }
})
